import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export const useAppointmentForm = ({ open, editing, prefill, onOpenChange, onSaved, empty, doctors }) => {
  const [form, setForm] = useState(empty);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lookedUp, setLookedUp] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    if (editing) setForm({ ...editing, phone: editing.phone || "", patient_age: String(editing.patient_age), amount: String(editing.amount) });
    else setForm({ ...empty, appointment_date: new Date().toISOString().slice(0, 16), ...(prefill || {}) });
    setPreview(editing?.prescription_path ? "existing" : null);
    setLookedUp(null);
  }, [open, editing, prefill, empty]);

  const onDoctorChange = (id) => {
    const doc = doctors.find((d) => d.id === id);
    setForm((f) => ({ ...f, doctor_id: id, amount: doc?.fee != null ? String(doc.fee) : f.amount }));
  };

  useEffect(() => {
    const digits = (form.phone || "").replace(/\D/g, "");
    if (editing || !open || digits.length < 10 || lookedUp === digits) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients/lookup", { params: { phone: form.phone } });
        setLookedUp(digits);
        if (!data) return;
        setForm((f) => ({ ...f, patient_name: data.patient_name, patient_age: String(data.patient_age ?? "") }));
        toast.success(`Returning patient: ${data.patient_name}`);
      } catch { /* lookup is best-effort */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form.phone, editing, open, lookedUp]);

  const attachImage = (data, file) => {
    setForm((f) => ({ ...f, prescription_path: data.path, prescription_filename: data.filename }));
    setPreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setForm((f) => ({ ...f, prescription_path: null, prescription_filename: null }));
    setPreview(null);
  };

  const validate = () => {
    if (!form.doctor_id) return "Select a doctor";
    if (!form.patient_name.trim()) return "Enter patient name";
    if (!form.appointment_date) return "Pick a date";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = { ...form, patient_age: parseInt(form.patient_age || 0, 10), amount: parseFloat(form.amount || 0) };
      if (editing) await api.patch(`/appointments/${editing.id}`, payload);
      else await api.post("/appointments", payload);
      toast.success(editing ? "Appointment updated" : "Appointment booked");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "Could not save appointment"));
    } finally {
      setSaving(false);
    }
  };

  return { form, set, preview, saving, onDoctorChange, attachImage, clearImage, submit };
};
